import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { useAuth } from "../context/AuthContext";

import api from "../utils/api";
import logo from "../assets/Logo_Creindia.jpg";

// --- CreIndia Brand Colors ---
const COLOR_BG = "#dbffe6";    // Lightest Green
const COLOR_BLUE = "#253dd0";  // Primary Blue/Indigo
const COLOR_GREEN = "#38852a"; // Secondary Green
const COLOR_ORANGE = "#fb7d47"; // Accent Orange
const COLOR_LIGHT_GREEN = "#f0fff4"; // Card internal accent

/* Animations */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const logoPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

const shake = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
  100% { transform: translateX(0); }
`;

/* Layout & Components */
const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: ${COLOR_BG}; /* Applying brand background */
`;

const Card = styled.form`
  width: 100%;
  max-width: 480px; /* Slightly wider card */
  background: #ffffff;
  border-radius: 18px; /* Softer rounded corners */
  box-shadow: 0 20px 50px rgba(37, 61, 208, 0.15); /* Blue-tinted shadow */
  padding: 3rem; /* Increased padding */
  text-align: center;
  animation: ${fadeUp} 600ms cubic-bezier(.2, .9, .2, 1) both;
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;

  @media (max-width: 500px) {
    padding: 1.5rem;
    border-radius: 14px;
  }
`;

const LogoImg = styled.img`
  height: 80px; /* Larger logo */
  margin-bottom: 1rem;
  display: block;
  margin-left: auto;
  margin-right: auto;
  animation: ${logoPulse} 4s ease-in-out infinite;
  object-fit: contain;
`;

const Title = styled.h2`
  margin: 0 0 0.25rem;
  color: ${COLOR_BLUE}; /* Brand Blue color */
  font-size: 2rem; /* Larger title */
  font-weight: 800;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem;
  color: ${COLOR_GREEN}; /* Brand Green color */
  font-size: 1rem;
  font-weight: 500;
`;

const Accent = styled.div`
  width: 70px; /* Wider accent line */
  height: 4px;
  border-radius: 99px;
  margin: 0.25rem auto 2rem;
  background: ${COLOR_ORANGE}; 
`;

const Field = styled.div`
  width: 100%;
  margin-bottom: 16px;
  text-align: left; /* Align labels/inputs left */
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 18px; /* Larger padding */
  border-radius: 12px;
  border: 2px solid rgba(14, 20, 30, 0.08); /* Thicker border */
  background: #f9f9f9;
  font-size: 16px;
  color: ${COLOR_BLUE};
  transition: all 200ms ease;
  font-weight: 500;
  margin-top: 4px;

  &:focus {
    border-color: ${COLOR_ORANGE};
    box-shadow: 0 0 0 3px rgba(251, 125, 71, 0.2); 
    background: #ffffff;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: ${COLOR_ORANGE};
  color: #fff;
  font-weight: 800;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 20px;
  font-size: 17px;
  transition: all 200ms ease;
  box-shadow: 0 10px 30px rgba(251, 125, 71, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 35px rgba(251, 125, 71, 0.35);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: translateY(0);
    box-shadow: none;
  }
`;

const Error = styled.div`
  color: #7f1d1d;
  background: #fee2e2;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 12px;
  font-weight: 600;
  animation: ${shake} 420ms ease;
  border: 1px solid #fca5a5;
`;

const Hint = styled.div`
  margin-top: 20px;
  font-size: 14px;
  color: #6b7280;
`;

const SecurityInfo = styled.div`
    margin-top: 20px;
    padding: 12px;
    border-radius: 12px;
    background: ${COLOR_LIGHT_GREEN};
    border: 1px solid ${COLOR_GREEN};
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${COLOR_GREEN};
`;

const Icon = styled.svg`
    width: 16px;
    height: 16px;
    margin-right: 8px;
    fill: ${COLOR_GREEN};
`;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth(); // Keep the original logic
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await api.post("/api/auth/login", {
        email,
        password,
        role: "dataAnalysis", // Keep role variable
      });

      if (res.data.success) { // Keep success check
        login(res.data.token);
        navigate("/dashboard");
      } else {
        setErr(res.data.message || "Invalid credentials");
      }
    } catch (error) {
      setErr(error.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Card onSubmit={submit}>
        <LogoImg src={logo} alt="CreIndia Logo" />
        <Title>Data Analysis Portal</Title>
        <Subtitle>Access crucial data insights for CreIndia.</Subtitle>
        <Accent />
        {err && <Error>{err}</Error>}

        <Field>
          <label style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>Email</label>
          <Input
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>

        <Field>
          <label style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>Password</label>
          <Input
            placeholder="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', fontSize: '14px' }}>
            <label style={{ display: 'flex', alignItems: 'center', color: '#6b7280' }}>
                <input 
                    type="checkbox" 
                    style={{ accentColor: COLOR_ORANGE, marginRight: '8px', height: '18px', width: '18px' }} 
                />
                Remember me
            </label>
             <a 
                href="#" 
                style={{ color: COLOR_ORANGE, textDecoration: 'none', fontWeight: '500' }} 
                onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
                Reset password
            </a>
        </div>


        <Button type="submit" disabled={loading}>
          {loading ? "Accessing Portal..." : "Secure Login"}
        </Button>

        <SecurityInfo>
            <Icon
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-7V7h2v4H9zm0 2h2v2H9v-2z"
                clipRule="evenodd"
              />
            </Icon>
            <span>Data integrity is our priority.</span>
        </SecurityInfo>

        <Hint>Use the credentials you created from SuperAdmin.</Hint>
      </Card>
    </Page>
  );
};

export default Login;